import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Chip, TextField } from "@mui/material";
import { Column, getColumnTypeConfig, getColumnTypeForPermission } from "../../types/table";
import { selectSalesforcePermissionById } from "../../support/slices/salesforcePermissionsSlice";
import { RootState } from "../../support/store";
import { useSelector } from "react-redux";
import StbModal from "../security-toolbox-ui/StbModal";


interface TableColumnManagerProps {
  columns?: {id: string, name: string}[];
  modalOpen: boolean;
  cancelSetColumns: () => Promise<void>;
  setColumns: (columns: {id: string, name: string}[]) => void;
}

const TableColumnManagerModal: React.FC<TableColumnManagerProps> = ({
  modalOpen,
  columns,
  cancelSetColumns,
  setColumns,
}: TableColumnManagerProps) => {
  const permissions = useSelector((state: RootState) => selectSalesforcePermissionById(state, 'Account'));

  // Chips
  const [selectedColumns, setSelectedColumns] = useState<{id: string, name: string}[]>(columns);
  useEffect(() => {
    setSelectedColumns(columns);
  }, [columns]);

  // Autocomplete 
  const [selectedColumn, setSelectedColumn] = useState<Column>(null);
  const [inputFieldValue, setInputFieldValue] = useState("");

  const availableColumns = useMemo(() => {
    return permissions.map(permission => ({
      id: permission.value,
      name: permission.name,
      columnType: getColumnTypeForPermission(permission),
      sortable: !!(permission?.source === 'salesforce')
    }) as Column)
    .filter(col => !selectedColumns.map((col) => col.id).includes(col.id));
  }, [permissions, selectedColumns]);

  const handleInputFieldChange = (label: string) => {
    setInputFieldValue(label.toLocaleLowerCase());
  }

  const handleSelectionChange = (column: Column) => {
    if (!column || selectedColumns.map((col) => col.name).includes(column.name)) return;

    const selected: Column = availableColumns.find(col => col.name === column.name);
    if (!selected) return;
  
    setSelectedColumns(prev => [...prev, getColumnTypeConfig(selected)]);
    setSelectedColumn(null);
    setInputFieldValue("");
  }

  const handleChipOnDelete = (column: {id: string, name: string}) => {
    setSelectedColumns(prev => prev.filter(col => col.id !== column.id));
  };

  const handleUpdateButtonOnClick = async () => {
    setColumns(selectedColumns);
  };

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [viewColumns, setViewColumns] = useState<{id: string, name: string}[]>(columns);

  return (
    <StbModal
      title="Manage Columns"
      role="default"
      submitButtonRole="save"
      submitLabel="Update Columns"
      open={modalOpen}
      onClose={cancelSetColumns}
      onSubmit={handleUpdateButtonOnClick}
    >
      <div className="flex flex-col">
        <Autocomplete
          options={availableColumns}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedColumn}
          onChange={(event, newValue) => {
            handleSelectionChange(newValue);
          }}
          inputValue={inputFieldValue}
          onInputChange={(event, newInputValue) => {
            handleInputFieldChange(newInputValue);
          }}

          renderInput={(params) => <TextField {...params} label="Select Table Column" />}
          fullWidth
          sx={{ pb: 2 }}
        />
        {/* <pre className='text-xs'>{JSON.stringify(selectedColumns.map(c => c.id), null, 2)}</pre>
        {searchTerm}
        <HbrSelect
          autocomplete={true}
          onHbr-change={(event) => {
            setViewColumns(event.target.value)
          }}
          clearable={true}
          onHbr-search={(event, exttr) => setSearchTerm(event.detail.value?.trim()?.toLocaleLowerCase() ?? '')}
          onHbr-clear={() => setViewColumns([])}
          className="hbr-css__form-section-md w-full"
          variant="tag"
          value={searchTerm}
        >
          {availableColumns?.map((column, idx) => (
            <HbrOption value={column.id} key={`${idx}-${column.id}`} allowSpace={true}>
              <HbrCheckbox
                onHbr-change={(event) => event.stopPropagation()}
                checked={viewColumns.map(c => c.id).includes(column.id)}
              >
                {inputFieldValue ? (
                  <HbrHighlight
                    text={column.name}
                    queryText={option.query}
                    continuous={false}
                  />
                ) : (
                  {column.name}
                )}
              </HbrCheckbox>
            </HbrOption>
          ))}
        </HbrSelect> */}

        <Box 
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            position: 'relative',
            pb: 2
          }}
        >
          {selectedColumns.filter(c => c.id !== 'selected').map((column) => (
            <Chip
              label={column.name}
              key={column.id}
              onDelete={!['Customer'].includes(column.name) ? () => handleChipOnDelete(column) : undefined}
              color={['Customer'].includes(column.name) ? 'primary' : 'default'}
              sx={{
                m: 0.5
              }}
            />
          ))}
        </Box>
      </div>
    </StbModal>
  );
};

export default TableColumnManagerModal;
